<template>
  <div class="settings-page">
    <h1>{{ t('settings.title') }}</h1>
    
    <!-- Tab Navigation -->
    <div class="tabs">
      <button
        class="tab-button"
        :class="{ active: activeTab === 'general' }"
        @click="activeTab = 'general'"
      >
        General
      </button>
      <button
        v-if="hasSpecificSettings"
        class="tab-button"
        :class="{ active: activeTab === 'specific' }"
        @click="activeTab = 'specific'"
      >
        Specific
      </button>
    </div>

    <!-- General Tab Content -->
    <div
      v-show="activeTab === 'general'"
      class="tab-content"
    >
      <form @submit.prevent="saveSettings">
        <div class="form-group">
          <label for="language">{{ t('settings.language') }}</label>
          <select
            id="language"
            v-model="currentLocale"
            @change="changeLocale"
          >
            <option
              v-for="loc in availableLocales"
              :key="loc"
              :value="loc"
            >
              {{ loc.toUpperCase() }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="theme">{{ t('settings.theme') }}</label>
          <div
            class="radio-group"
            id="theme"
          >
            <label>
              <input
                type="radio"
                name="theme"
                value="light"
                v-model="theme"
              >
              {{ t('settings.light') }}
            </label>
            <label>
              <input
                type="radio"
                name="theme"
                value="dark"
                v-model="theme"
              >
              {{ t('settings.dark') }}
            </label>
          </div>
        </div>
        <div class="form-group">
          <label for="layout">{{ t('settings.layout') }}</label>
          <div
            class="layout-selector"
            id="layout"
          >
            <label
              class="layout-option"
              :class="{ 'selected': layout === 'sidebar' }"
            >
              <input
                type="radio"
                name="layout"
                value="sidebar"
                v-model="layout"
                style="display: none;"
              >
              <div class="layout-preview">
                <svg
                  width="60"
                  height="40"
                  viewBox="0 0 120 80"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <!-- Sidebar -->
                  <rect
                    x="2"
                    y="2"
                    width="30"
                    height="76"
                    rx="2"
                    fill="var(--color-navbar-bg)"
                    stroke="currentColor"
                  />
                  <!-- Content area -->
                  <rect
                    x="36"
                    y="2"
                    width="82"
                    height="76"
                    rx="2"
                    fill="var(--color-bg)"
                    stroke="currentColor"
                  />
                  <!-- Sidebar items -->
                  <line
                    x1="8"
                    y1="12"
                    x2="26"
                    y2="12"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <line
                    x1="8"
                    y1="20"
                    x2="26"
                    y2="20"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <line
                    x1="8"
                    y1="28"
                    x2="26"
                    y2="28"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <!-- Content lines -->
                  <line
                    x1="42"
                    y1="12"
                    x2="110"
                    y2="12"
                    stroke="currentColor"
                    stroke-width="1"
                    opacity="0.5"
                  />
                  <line
                    x1="42"
                    y1="20"
                    x2="100"
                    y2="20"
                    stroke="currentColor"
                    stroke-width="1"
                    opacity="0.5"
                  />
                  <line
                    x1="42"
                    y1="28"
                    x2="110"
                    y2="28"
                    stroke="currentColor"
                    stroke-width="1"
                    opacity="0.5"
                  />
                </svg>
              </div>
              <span class="layout-label">{{ t('settings.sidebar') }}</span>
            </label>
            <label
              class="layout-option"
              :class="{ 'selected': layout === 'navbar' }"
            >
              <input
                type="radio"
                name="layout"
                value="navbar"
                v-model="layout"
                style="display: none;"
              >
              <div class="layout-preview">
                <svg
                  width="60"
                  height="40"
                  viewBox="0 0 120 80"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <!-- Navbar -->
                  <rect
                    x="2"
                    y="2"
                    width="116"
                    height="20"
                    rx="2"
                    fill="var(--color-navbar-bg)"
                    stroke="currentColor"
                  />
                  <!-- Content area -->
                  <rect
                    x="2"
                    y="26"
                    width="116"
                    height="52"
                    rx="2"
                    fill="var(--color-bg)"
                    stroke="currentColor"
                  />
                  <!-- Navbar items -->
                  <line
                    x1="8"
                    y1="12"
                    x2="20"
                    y2="12"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <line
                    x1="28"
                    y1="12"
                    x2="40"
                    y2="12"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <line
                    x1="48"
                    y1="12"
                    x2="60"
                    y2="12"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <!-- Content lines -->
                  <line
                    x1="8"
                    y1="36"
                    x2="110"
                    y2="36"
                    stroke="currentColor"
                    stroke-width="1"
                    opacity="0.5"
                  />
                  <line
                    x1="8"
                    y1="44"
                    x2="100"
                    y2="44"
                    stroke="currentColor"
                    stroke-width="1"
                    opacity="0.5"
                  />
                  <line
                    x1="8"
                    y1="52"
                    x2="110"
                    y2="52"
                    stroke="currentColor"
                    stroke-width="1"
                    opacity="0.5"
                  />
                </svg>
              </div>
              <span class="layout-label">{{ t('settings.navbar') }}</span>
            </label>
          </div>
        </div>
        <div class="form-group experiments">
          <label
            class="checkbox-inline"
            for="experimentations"
          >
            <input
              type="checkbox"
              id="experimentations"
              v-model="experimentationsEnabled"
              @change="onExperimentationToggle"
            >
            {{ t('settings.experimentations') }}
          </label>
          <div
            v-if="experimentationsEnabled"
            class="experimentations-list"
          >
            <label
              v-for="exp in availableExperimentations"
              :key="exp.key"
              class="checkbox-inline experimentation-item"
              :for="`exp-${exp.key}`"
            >
              <input
                type="checkbox"
                :id="`exp-${exp.key}`"
                :value="exp.key"
                v-model="selectedExperimentations"
              >
              {{ exp.label }}
            </label>
          </div>
        </div>
        <div class="form-group help">
          <label
            class="checkbox-inline"
            for="helpEnabled"
          >
            <input
              type="checkbox"
              id="helpEnabled"
              v-model="helpEnabled"
            >
            {{ t('settings.helpEnabled') }}
          </label>
        </div>
        <button type="submit">
          {{ t('settings.save') }}
        </button>
      </form>
      <div
        v-if="message"
        class="message"
      >
        {{ t('settings.saved') }}
      </div>
    </div>

    <!-- Specific Tab Content -->
    <div
      v-show="activeTab === 'specific'"
      class="tab-content"
    >
      <slot name="service-specific-settings" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, useSlots, computed } from 'vue';
import { localStorageManager } from '../utils/localstorage'
import { useI18n } from 'vue-i18n';
import { r7eBaseApp } from '@r7e/shared';
import { useLocaleLoader } from '../composables/useLocaleLoader';
const { t } = useI18n();

const slots = useSlots();
const hasSpecificSettings = computed(() => {
  return Boolean(slots['service-specific-settings']);
});

const { locale } = useI18n();
const { loadLocaleIfNeeded } = useLocaleLoader();
const r7eApp = r7eBaseApp.getMainApp ? r7eBaseApp.getMainApp() : r7eBaseApp;

const activeTab = ref('general');
const theme = ref(localStorageManager.getItem('theme') || 'light');
const layout = ref(r7eBaseApp.getUILayout());
const helpEnabled = ref(r7eApp.isHelpEnabled ? r7eApp.isHelpEnabled() : false);
const message = ref('');

// Experimentations configuration
const availableExperimentations = [];
for (const exp of r7eApp.getAvailableExperimentations()) {
  availableExperimentations.push({ key: exp, label: exp });
}

// Parse experimentations using r7eApp methods
const parseExperimentations = () => {
  const stored = r7eApp.getExperimentations ? r7eApp.getExperimentations() : null;
  if (!stored || stored === 'false' || stored === '') {
    return { enabled: false, selected: [] };
  }
  if (stored === 'true') {
    return { enabled: true, selected: availableExperimentations.map(e => e.key) };
  }
  // Try to parse as JSON array
  try {
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return { enabled: parsed.length > 0, selected: parsed };
    }
  } catch (e) {
    // If parsing fails, treat as disabled
  }
  return { enabled: false, selected: [] };
};

const experimentationsState = parseExperimentations();
const experimentationsEnabled = ref(experimentationsState.enabled);
const selectedExperimentations = ref([...experimentationsState.selected]);

const onExperimentationToggle = () => {
  if (experimentationsEnabled.value) {
    // When enabling, select all by default if none are selected
    if (selectedExperimentations.value.length === 0) {
      selectedExperimentations.value = availableExperimentations.map(e => e.key);
    }
  } else {
    // When disabling, clear all selections
    selectedExperimentations.value = [];
  }
};

// Watch selectedExperimentations - if all are unchecked, disable global checkbox
watch(selectedExperimentations, (newVal) => {
  if (experimentationsEnabled.value && newVal.length === 0) {
    experimentationsEnabled.value = false;
  }
});

// Watch hasSpecificSettings - if slot becomes empty while on specific tab, switch to general
watch(hasSpecificSettings, (hasContent) => {
  if (!hasContent && activeTab.value === 'specific') {
    activeTab.value = 'general';
  }
});

const availableLocales = r7eApp.getAvailableLocales ? r7eApp.getAvailableLocales() : ['en', 'fr', 'de', 'es', 'it', 'ja', 'zh'];
const currentLocale = ref(r7eApp.getLocale ? r7eApp.getLocale() : (locale?.value || 'en'));

const changeLocale = async () => {
  if (currentLocale?.value) {
    // Load locale if needed (lazy loading)
    await loadLocaleIfNeeded(currentLocale.value);
    if (r7eApp.setLocale) {
      r7eApp.setLocale(currentLocale.value);
    }
  }
};

const saveSettings = () => {
  // Placeholder for saving logic
  // Apply theme change immediately
  let reload = false;
  let reload_theme = false;
  const applyTheme = (t) => {
    const root = document.documentElement;
    root.classList.remove('theme-dark', 'theme-light');
    root.classList.add(`theme-${t}`);
  };
  // Save to localStorage
  if (localStorageManager.getItem('theme') !== theme.value) {
    localStorageManager.setItem('theme', theme.value);
    reload_theme = true;
  }

  if (r7eApp.getUILayout() !== layout.value) {
    r7eApp.setUILayout(layout.value);
    reload = true;
  }
  
  // Save experimentations using r7eApp methods
  const currentExpValue = r7eApp.getExperimentations ? r7eApp.getExperimentations() : null;
  let newExpValue = '';
  if (experimentationsEnabled.value && selectedExperimentations.value.length > 0) {
    // If all are selected, store "true", otherwise store JSON array
    if (selectedExperimentations.value.length === availableExperimentations.length) {
      newExpValue = 'true';
    } else {
      newExpValue = JSON.stringify(selectedExperimentations.value);
    }
  } else {
    // Disabled or no items selected - remove or set to empty
    newExpValue = '';
  }
  
  if (currentExpValue !== newExpValue) {
    if (r7eApp.setExperimentations) {
      r7eApp.setExperimentations(newExpValue);
    }
    reload = true;
  }
  if (r7eApp.isHelpEnabled && r7eApp.isHelpEnabled() !== helpEnabled.value) {
    r7eApp.setHelpEnabled(helpEnabled.value);
    reload = true;
  }
  message.value = t('settings.saved');
  if (reload_theme) {
    // Apply theme change right away when value changes
    applyTheme(theme.value);
  }
  if (reload) {
    window.location.reload();
  }
};
</script>

<style scoped>
.settings-page {
  max-width: 500px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-bg);
  color: var(--color-text);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.settings-page h1 {
  margin-bottom: 1.5rem;
}
.form-group {
  margin-bottom: 1.2rem;
}
label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}
input:not([type="radio"]):not([type="checkbox"]), select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
}
.radio-group {
  display: flex;
  gap: 1rem;
}
.radio-group label {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  margin-bottom: 0;
}

.layout-selector {
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
}

.layout-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 1rem;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  transition: all 0.3s;
  background: var(--color-bg);
  flex: 1;
  max-width: 150px;
}

.layout-option:hover {
  border-color: var(--color-green);
  background: color-mix(in srgb, var(--color-green), transparent 95%);
}

.layout-option.selected {
  border-color: var(--color-green);
  background: color-mix(in srgb, var(--color-green), transparent 90%);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-green), transparent 80%);
}

.layout-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  opacity: 0.7;
}

.layout-option.selected .layout-preview {
  opacity: 1;
  color: var(--color-green);
}

.layout-label {
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  color: var(--color-text);
}

.layout-option.selected .layout-label {
  color: var(--color-green);
  font-weight: 600;
}
.checkbox-inline {
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
}
button {
  background: var(--color-safe-bg);
  color: var(--color-safe-text);
  border: none;
  padding: 0.7rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}
button:hover {
  background: var(--color-safe-bg);
}
.message {
  margin-top: 1rem;
  color: var(--color-safe);
}
div.experiments {
  border-top: 1px solid var(--color-border);
  padding-top: 1rem;
  margin: 1rem 0;
}

.experimentations-list {
  margin-top: 1rem;
  margin-left: 1.5rem;
  padding-left: 1rem;
  border-left: 2px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.experimentation-item {
  margin-bottom: 0;
}
div.help {
  border-top: 1px solid var(--color-border);
  padding-top: 1rem;
  margin: 1rem 0;
}

.tabs {
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid var(--color-border);
  margin-bottom: 1.5rem;
}

.tab-button {
  background: transparent;
  border: none;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-size: 1rem;
  color: var(--color-text);
  opacity: 0.7;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
}

.tab-button:hover {
  opacity: 1;
  color: var(--color-green);
}

.tab-button.active {
  opacity: 1;
  color: var(--color-green);
  border-bottom-color: var(--color-green);
  font-weight: 600;
}

.tab-content {
  min-height: 200px;
}
</style> 